import React, { useEffect } from "react";
import { useSprings } from "react-spring";
import { useDrag } from "react-use-gesture";
import { isMobile } from "react-device-detect";
import Card from "./card";
import { useSetState } from "../../helpers";

import "./style.css";
import httpUtils from "../../utils/http.utils";
import config from "../../config";

const to = () => ({
  x: 0,
  y: 0,
  opacity: 0,
  scale: 1,
  rot: -12 + Math.random() * 24,
  delay: undefined,
});

const from = () => ({
  x: 0,
  y: 0,
  opacity: 0,
  scale: 1,
  rot: 0,
  delay: undefined,
});

const trans = (r, s) => `perspective(2000px) rotateX(12deg) rotateY(${r / 12}deg) rotateZ(${r}deg) scale(${s})`;

const RenderCards = ({ cards, bind, data, isLoading }) => ((
  <div className={["cards", isLoading ? "hide" : "show"].join(" ")}>
    {
      cards.map(({ x, y, rot, scale, opacity }, i) => ((
        <Card
          key={i}
          i={i}
          x={x}
          y={y}
          rot={rot}
          opacity={opacity}
          scale={scale}
          trans={trans}
          data={data}
          bind={bind}
        />
      ))
      )
    }
  </div>
));

const Moderator = () => {
  // The set flags all the cards that are flicked out
  const [state, setState] = useSetState({
    loading: true,
    data: [],
    gone: new Set(),
    total: null,
  });

  // Create a bunch of springs using the helpers above
  const [props, set] = useSprings(state.data.length, i => ({
    ...to(i),
    from: from(i),
  }));

  useEffect(() => {
    getUserImages();

    window.addEventListener("visibilitychange", handleActivity);

    return () => {
      window.removeEventListener("visibilitychange", handleActivity);
    };
  }, []);

  // User has switched away from the tab (AKA tab is hidden)
  const handleActivity = (forcedFlag) => {
    if (typeof forcedFlag === 'boolean') {
      return forcedFlag && getUserImages();
    }

    return !document.hidden && getUserImages();
  };

  const handleKeys = (key) => {
    const dir = key === "d" ? -1 : key === "l" ? 1 : null;

    if (!dir || state.loading) return;

    const index = (state.data.length - 1) - state.gone.size;

    state.gone.add(index);

    set(i => {
      // We"re only interested in changing spring-data for the current spring
      if (index !== i) return;

      const x = (200 + window.innerWidth) * dir;
      // How much the card tilts, flicking it harder makes it rotate faster

      const rot = 10 + dir * 10;

      // Active cards lift up a bit
      const scale = 1.1;

      return {
        x,
        rot,
        scale,
        opacity: 1,
        delay: undefined,
        config: {
          friction: 50,
          tension: 70
        }
      }
    });

    saveParseData(index, dir === 1);
  }

  const getUserImages = async () => {
    await setState(state => {
      state.loading = true;
    });

    try {
      const results = (await httpUtils.get(`${config.BACK_API}/api/moderation`)).data.results;
      const count = results.length;
      await setState(state => {
        state.gone = new Set();
        state.data = results;
        state.total = count;
      });

      set(i => {
        return {
          x: 0,
          y: 0,
          opacity: 0,
          scale: 1,
          rot: -12 + Math.random() * 24,
          delay: undefined,
          config: {
            friction: 50,
            tension: 0
          }
        }
      });

      setTimeout(() => {
        setState(state => {
          state.loading = false;
        });
      }, 600);
    } catch (e) {
      console.error(e);
    }
  }

  const saveParseData = async (index, action) => {
    const id = state.data[index].id;
    try {
      const status = action ? 'OK' : 'KO';
      await httpUtils.put(`${config.BACK_API}/api/moderation/${id}/${status}`)

      setState(state => ({
        ...state,
        total: state.total - 1
      }));
    } catch (e) {
      console.error(e);
    }

    if (
      state.gone.size === state.data.length
    ) {
      getUserImages();
    }
  }

  const bind = useDrag(({ args: [index], down, movement: [mx, my], distance, direction: [xDir], velocity }) => {
    // If you flick hard enough it should trigger the card to fly out
    const trigger = velocity > 0.2;

    // Direction should either point left or right
    const dir = xDir < 0 ? -1 : 1;

    // If button/finger"s up and trigger velocity is reached, we flag the card ready to fly out
    if (!down && trigger) {
      state.gone.add(index);
      saveParseData(index, dir === 1);
    }

    set(i => {
      // We"re only interested in changing spring-data for the current spring
      if (index !== i) return;


      const isGone = state.gone.has(index);
      // When a card is gone it flys out left or right, otherwise goes back to zero
      const x = isGone ? (200 + window.innerWidth) * dir : down ? mx : 0;

      // How much the card tilts, flicking it harder makes it rotate faster
      const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0);

      // Active cards lift up a bit
      const scale = down ? 1.1 : 1;

      const opacity = x === 0 ? 0 : (distance <= 100 ? (distance / 100) : 1);

      return {
        x,
        rot,
        scale,
        opacity: opacity,
        delay: undefined,
        config: {
          friction: 50,
          tension: down ? 1000 : isGone ? 200 : 500
        }
      }
    });
  });

  const logOutHandler = (e) => {
    e.preventDefault();

    // Parse.User.logOut();
    // location.reload();
  }

  return (
    <>
      <div className="allDoneMessage" style={{ opacity: state.total === 0 ? 1 : 0 }}>
        All Done! :)
      </div>

      <div className="loading" style={{ opacity: state.loading ? 1 : 0 }} />

      <RenderCards cards={props} bind={bind} data={state.data} isLoading={state.loading} />

      <div className="controls" style={{ opacity: state.total > 0 ? 1 : 0 }}>
        <div className="button" onClick={() => handleKeys("d")}><i className="icon reject icofont-close-circled" /></div>
        <div className="count">{state.total} images left</div>
        <div className="button" onClick={() => handleKeys("l")}><i className="icon accept icofont-check-circled" /></div>
      </div>
    </>
  );
}

export default Moderator;
